import React, { useState, useMemo, useEffect, useRef } from 'react';
import { ExternalLink, ChevronUp, ChevronDown, ChevronsUpDown, X, Copy, CheckCircle2, TriangleAlert, Feather, Flame, Siren, Puzzle, Tag, Info } from 'lucide-react';

import { DUP_LINK_TYPES, LINK_CHECK_LABELS } from './duplicateLinks';

const JIRA_BASE = 'https://contentsquare.atlassian.net/browse';

// One place for what the column means — this came up enough in practice (confusion over "—" vs
// "Not linked") that it's worth spelling out right on the header, not just in a code comment.
const LINK_CHECK_TOOLTIP = [
  'Linked: resolved as Duplicate and has a Jira link.',
  "Not linked: resolved as Duplicate but missing the link — the actual gap to fix.",
  "Link, no resolution: has a link but isn't resolved as Duplicate.",
  '—: neither — nobody has reviewed this bug as a duplicate yet.'
].join('\n');
const LS_KEY = 'dupetickets_columns';
const LS_SORT_KEY = 'dupetickets_sort';
const DEFAULT_SORT = { col: 'matchCount', dir: 'desc' };

const COLUMNS = [
  { label: 'Key',        key: 'key',        required: true },
  { label: 'Title',      key: 'summary',    required: true },
  { label: 'Matches',    key: 'matchCount', required: true },
  { label: 'Link check', key: 'linkCheck' },
  { label: 'Project',    key: 'project' },
  { label: 'Status',     key: 'status' },
  { label: 'Resolution', key: 'resolution' },
  { label: 'Severity',   key: 'severity',  defaultHidden: true },
  { label: 'Component',  key: 'component', defaultHidden: true },
  { label: 'Unit',       key: 'unit',      defaultHidden: true },
  { label: 'Reporter',   key: 'reporter' },
  { label: 'Assignee',   key: 'assignee',  defaultHidden: true },
  { label: 'Created',    key: 'created' },
];

function severityRank(val) {
  const lower = (val ?? '').toLowerCase();
  if (lower.includes('critical')) return 3;
  if (lower.includes('major')) return 2;
  if (lower.includes('minor')) return 1;
  return 0;
}

function formatDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

function loadSort() {
  try {
    const stored = localStorage.getItem(LS_SORT_KEY);
    if (stored) return JSON.parse(stored);
  } catch {}
  return DEFAULT_SORT;
}

function loadVisibleColumns() {
  try {
    const stored = localStorage.getItem(LS_KEY);
    if (stored) {
      const keys = JSON.parse(stored);
      const required = COLUMNS.filter(c => c.required).map(c => c.key);
      return [...new Set([...required, ...keys])];
    }
  } catch {}
  return COLUMNS.filter(c => !c.defaultHidden).map(c => c.key);
}

function statusClass(status) {
  if (!status) return 'default';
  if (status === 'Done') return 'done';
  if (status === 'Backlog') return 'backlog';
  return 'default';
}

function SortIcon({ col, sortCol, sortDir }) {
  if (sortCol !== col) return <ChevronsUpDown size={12} className="sort-icon inactive" />;
  return sortDir === 'asc'
    ? <ChevronUp size={12} className="sort-icon" />
    : <ChevronDown size={12} className="sort-icon" />;
}

function ColumnSelector({ visibleKeys, onChange, onClose }) {
  const ref = useRef(null);

  useEffect(() => {
    function handleClick(e) {
      if (ref.current && !ref.current.contains(e.target)) onClose();
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [onClose]);

  function toggle(key) {
    const col = COLUMNS.find(c => c.key === key);
    if (col?.required) return;
    onChange(visibleKeys.includes(key) ? visibleKeys.filter(k => k !== key) : [...visibleKeys, key]);
  }

  return (
    <div className="column-selector-popup" ref={ref}>
      <div className="column-selector-header">
        <span>Columns</span>
        <button className="column-selector-close" onClick={onClose}><X size={14} /></button>
      </div>
      {COLUMNS.map(col => (
        <label key={col.key} className={`column-selector-item${col.required ? ' required' : ''}`}>
          <input
            type="checkbox"
            checked={visibleKeys.includes(col.key)}
            disabled={col.required}
            onChange={() => toggle(col.key)}
          />
          {col.label}
        </label>
      ))}
    </div>
  );
}

function LinkCheckCell({ bug }) {
  // linkState is computed server-side (DuplicatesService.linkState) so the rule lives in one
  // place; the links themselves are only needed for the tooltip.
  const state = bug.linkState;
  const links = (bug.duplicateLinks ?? []).filter(l => DUP_LINK_TYPES.includes(l.link));
  const label = LINK_CHECK_LABELS[state];

  if (state === 'none') return <span className="dup-muted">—</span>;

  const title = links.length
    ? links.map(l => `${l.link} ${l.linkedIssue}`).join(', ')
    : 'Resolved as Duplicate but no duplicate link exists in Jira';

  if (state === 'ok') {
    return <span className="dup-linkcheck ok" title={title}><CheckCircle2 size={13} />{label}</span>;
  }
  if (state === 'missing') {
    return <span className="dup-linkcheck missing" title={title}><TriangleAlert size={13} />{label}</span>;
  }
  return <span className="dup-linkcheck warn" title={title}><TriangleAlert size={13} />{label}</span>;
}

export default function BugsTable({ bugs, showColumnSelector, onCloseColumnSelector, onOpenMatches, maxMatches }) {
  const [sortCol, setSortCol] = useState(() => loadSort().col);
  const [sortDir, setSortDir] = useState(() => loadSort().dir);
  const [visibleKeys, setVisibleKeys] = useState(loadVisibleColumns);

  function handleSort(key) {
    const newDir = sortCol === key ? (sortDir === 'asc' ? 'desc' : 'asc') : 'asc';
    setSortCol(key);
    setSortDir(newDir);
    try { localStorage.setItem(LS_SORT_KEY, JSON.stringify({ col: key, dir: newDir })); } catch {}
  }

  function handleColumnChange(keys) {
    setVisibleKeys(keys);
    try { localStorage.setItem(LS_KEY, JSON.stringify(keys)); } catch {}
  }

  const visibleCols = COLUMNS.filter(c => visibleKeys.includes(c.key));

  const sorted = useMemo(() => {
    return [...bugs].sort((a, b) => {
      if (sortCol === 'matchCount') {
        const diff = a.matches.length - b.matches.length;
        return sortDir === 'asc' ? diff : -diff;
      }
      if (sortCol === 'severity') {
        const diff = severityRank(a.severity) - severityRank(b.severity);
        return sortDir === 'asc' ? diff : -diff;
      }
      if (sortCol === 'created') {
        const at = a.created ? new Date(a.created).getTime() : 0;
        const bt = b.created ? new Date(b.created).getTime() : 0;
        return sortDir === 'asc' ? at - bt : bt - at;
      }
      if (sortCol === 'linkCheck') {
        const order = { missing: 0, 'linked-only': 1, ok: 2, none: 3 };
        const diff = order[a.linkState] - order[b.linkState];
        return sortDir === 'asc' ? diff : -diff;
      }
      const av = (a[sortCol] ?? '').toString().toLowerCase();
      const bv = (b[sortCol] ?? '').toString().toLowerCase();
      return sortDir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
    });
  }, [bugs, sortCol, sortDir]);

  return (
    <div style={{ position: 'relative' }}>
      {showColumnSelector && (
        <ColumnSelector
          visibleKeys={visibleKeys}
          onChange={handleColumnChange}
          onClose={onCloseColumnSelector}
        />
      )}
      <div className="issues-table-wrap">
        <table className="issues-table">
          <thead>
            <tr>
              {visibleCols.map(col => (
                <th key={col.key} onClick={() => handleSort(col.key)} className="sortable-th">
                  {col.label}
                  {col.key === 'linkCheck' && (
                    <Info
                      size={12}
                      className="link-check-info"
                      title={LINK_CHECK_TOOLTIP}
                      onClick={e => e.stopPropagation()}
                    />
                  )}
                  <SortIcon col={col.key} sortCol={sortCol} sortDir={sortDir} />
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sorted.map(bug => (
              <tr key={bug.key}>
                {visibleCols.map(col => {
                  switch (col.key) {
                    case 'key':
                      return (
                        <td key="key" style={{ whiteSpace: 'nowrap' }}>
                          <a
                            href={`${JIRA_BASE}/${bug.key}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: 'var(--cmp-primary)', fontWeight: 600, textDecoration: 'none' }}
                          >
                            <ExternalLink size={15} />
                            {bug.key}
                          </a>
                        </td>
                      );
                    case 'summary': {
                      const sev = (bug.severity ?? '').toLowerCase();
                      const showSeverityPill = !visibleKeys.includes('severity');
                      const severityPill = showSeverityPill && (sev.includes('critical')
                        ? <span className="issue-type-pill severity-critical"><Siren size={11} />Critical</span>
                        : sev.includes('major')
                        ? <span className="issue-type-pill severity-major"><Flame size={11} />Major</span>
                        : sev.includes('minor')
                        ? <span className="issue-type-pill severity-minor"><Feather size={11} />Minor</span>
                        : null);
                      const showUnitPill = !visibleKeys.includes('unit');
                      const unitPill = showUnitPill && bug.unit
                        ? <span className="issue-type-pill unit"><Tag size={11} />{bug.unit}</span>
                        : null;
                      return (
                        <td key="summary">
                          <div className="issue-cell-link">
                            <div className="issue-cell-title">{bug.summary}</div>
                            {(severityPill || unitPill) && (
                              <div className="issue-cell-component">{severityPill}{unitPill}</div>
                            )}
                          </div>
                        </td>
                      );
                    }
                    case 'matchCount': {
                      const n = bug.matches.length;
                      if (n === 0) {
                        return (
                          <td key="matchCount">
                            <button
                              className="dup-count-btn dup-count-btn-empty"
                              onClick={() => onOpenMatches(bug)}
                              title="No matches found — click to link a duplicate manually"
                            >
                              —
                            </button>
                          </td>
                        );
                      }
                      return (
                        <td key="matchCount">
                          <button
                            className={`dup-count-btn${n > 5 ? ' over-jira-cap' : ''}`}
                            onClick={() => onOpenMatches(bug)}
                            title={n > 5
                              ? `${n} matches — more than the 5 stored per source row, because links are read from both directions`
                              : `${n} potential duplicate${n !== 1 ? 's' : ''}`}
                          >
                            <Copy size={12} />
                            {n}
                            {maxMatches ? <span className="dup-count-cap">/{maxMatches}</span> : null}
                          </button>
                        </td>
                      );
                    }
                    case 'linkCheck':
                      return <td key="linkCheck"><LinkCheckCell bug={bug} /></td>;
                    case 'project':
                      return <td key="project">{bug.project ?? '—'}</td>;
                    case 'status':
                      return <td key="status"><span className={`status-chip ${statusClass(bug.status)}`}>{bug.status ?? '—'}</span></td>;
                    case 'resolution':
                      return (
                        <td key="resolution">
                          {bug.resolution
                            ? <span className={`status-chip ${bug.resolution === 'Done' ? 'done' : 'resolution-unresolved'}`}>{bug.resolution}</span>
                            : <span className="dup-muted">—</span>}
                        </td>
                      );
                    case 'severity': {
                      const s = (bug.severity ?? '').toLowerCase();
                      return (
                        <td key="severity">
                          {s.includes('critical')
                            ? <span className="issue-type-pill severity-critical"><Siren size={11} />Critical</span>
                            : s.includes('major')
                            ? <span className="issue-type-pill severity-major"><Flame size={11} />Major</span>
                            : s.includes('minor')
                            ? <span className="issue-type-pill severity-minor"><Feather size={11} />Minor</span>
                            : '—'}
                        </td>
                      );
                    }
                    case 'component': {
                      const parts = bug.component
                        ? bug.component.split(',').map(c => c.trim().replace(/^"|"$/g, '')).filter(Boolean)
                        : [];
                      return (
                        <td key="component">
                          {parts.length
                            ? <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                                {parts.map(c => <span key={c} className="issue-type-pill component"><Puzzle size={11} />{c}</span>)}
                              </div>
                            : '—'}
                        </td>
                      );
                    }
                    case 'unit':
                      return <td key="unit">{bug.unit ? <span className="issue-type-pill unit"><Tag size={11} />{bug.unit}</span> : '—'}</td>;
                    case 'reporter':
                      return <td key="reporter">{bug.reporter ?? '—'}</td>;
                    case 'assignee':
                      return <td key="assignee">{bug.assignee ?? '—'}</td>;
                    case 'created':
                      return <td key="created" style={{ whiteSpace: 'nowrap', color: 'var(--cmp-muted)', fontSize: '0.85em' }}>{formatDate(bug.created)}</td>;
                    default:
                      return <td key={col.key} />;
                  }
                })}
              </tr>
            ))}
          </tbody>
        </table>
        {sorted.length === 0 && (
          <div className="dup-muted" style={{ padding: 24, textAlign: 'center' }}>
            No bugs match the current filters.
          </div>
        )}
      </div>
    </div>
  );
}
